<template>
  <div class="section">
    <div class="container">
      <div class="page-header">
        <h1 class="page-title">บัญชีของฉัน</h1>
        <button @click="logout" class="btn btn-ghost logout-btn">ออกจากระบบ</button>
      </div>

      <div class="tabs-nav">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id" class="tab-btn" :class="{ active: activeTab === tab.id }">{{ tab.label }}</button>
      </div>

      <div v-if="loading" class="text-center" style="padding: 3rem 0;">
        <div class="loading-spinner" style="margin: 0 auto;"></div>
      </div>

      <div v-else class="tab-content">
        <div v-if="activeTab === 'profile'" class="profile-section">
          <div class="card">
            <div class="card-header"><h3 class="card-title">ข้อมูลส่วนตัว</h3></div>
            <div class="card-body">
              <Form @submit="updateProfile" :validation-schema="profileSchema" v-slot="{ errors, isSubmitting }">
                <div class="form-grid">
                  <div class="form-group">
                    <label class="form-label">ชื่อ-นามสกุล *</label>
                    <Field name="name" v-model="profileForm.name" type="text" class="form-input" :class="{ 'input-error': errors.name }" />
                    <ErrorMessage name="name" class="error-text" />
                  </div>
                  <div class="form-group">
                    <label class="form-label">ชื่อผู้ใช้</label>
                    <input type="text" :value="user?.username" class="form-input" disabled />
                    <span class="help-text">ไม่สามารถเปลี่ยนชื่อผู้ใช้ได้</span>
                  </div>
                  <div class="form-group">
                    <label class="form-label">อีเมล</label>
                    <Field name="email" v-model="profileForm.email" type="email" class="form-input" :class="{ 'input-error': errors.email }" placeholder="example@email.com" />
                    <ErrorMessage name="email" class="error-text" />
                  </div>
                  <div class="form-group">
                    <label class="form-label">เบอร์โทรศัพท์</label>
                    <Field name="phone" v-model="profileForm.phone" type="tel" class="form-input" :class="{ 'input-error': errors.phone }" placeholder="0812345678" />
                    <ErrorMessage name="phone" class="error-text" />
                  </div>
                </div>
                <button type="submit" :disabled="isSubmitting" class="btn btn-primary">{{ isSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล' }}</button>
              </Form>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'address'" class="address-section">
          <div class="card">
            <div class="card-header">
              <h3 class="card-title">ที่อยู่จัดส่ง</h3>
              <p class="card-subtitle">ที่อยู่นี้จะถูกใช้เป็นค่าเริ่มต้นเมื่อสั่งซื้อสินค้า</p>
            </div>
            <div class="card-body">
              <Form @submit="updateAddress" :validation-schema="addressSchema" v-slot="{ errors, isSubmitting }">
                <div class="form-group">
                  <label class="form-label">ที่อยู่ (บ้านเลขที่ ซอย ถนน)</label>
                  <Field as="textarea" name="address" v-model="addressForm.address" class="form-input form-textarea" :class="{ 'input-error': errors.address }" rows="3" placeholder="123/45 ซอยสุขุมวิท 55" />
                  <ErrorMessage name="address" class="error-text" />
                </div>
                <div class="form-grid">
                  <div class="form-group">
                    <label class="form-label">เขต/อำเภอ</label>
                    <Field name="district" v-model="addressForm.district" type="text" class="form-input" :class="{ 'input-error': errors.district }" placeholder="วัฒนา" />
                    <ErrorMessage name="district" class="error-text" />
                  </div>
                  <div class="form-group">
                    <label class="form-label">จังหวัด</label>
                    <Field name="province" v-model="addressForm.province" type="text" class="form-input" :class="{ 'input-error': errors.province }" placeholder="กรุงเทพมหานคร" />
                    <ErrorMessage name="province" class="error-text" />
                  </div>
                  <div class="form-group">
                    <label class="form-label">รหัสไปรษณีย์</label>
                    <Field name="postal_code" v-model="addressForm.postal_code" type="text" class="form-input" :class="{ 'input-error': errors.postal_code }" placeholder="10110" maxlength="5" />
                    <ErrorMessage name="postal_code" class="error-text" />
                  </div>
                </div>
                <button type="submit" :disabled="isSubmitting" class="btn btn-primary">{{ isSubmitting ? 'กำลังบันทึก...' : 'บันทึกที่อยู่' }}</button>
              </Form>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'password'" class="password-section">
          <div class="card">
            <div class="card-header"><h3 class="card-title">เปลี่ยนรหัสผ่าน</h3></div>
            <div class="card-body">
              <Form @submit="changePassword" :validation-schema="passwordSchema" v-slot="{ errors, isSubmitting }">
                <div class="form-group">
                  <label class="form-label">รหัสผ่านปัจจุบัน *</label>
                  <Field name="current_password" v-model="passwordForm.current_password" type="password" class="form-input" :class="{ 'input-error': errors.current_password }" />
                  <ErrorMessage name="current_password" class="error-text" />
                </div>
                <div class="form-group">
                  <label class="form-label">รหัสผ่านใหม่ *</label>
                  <Field name="password" v-model="passwordForm.password" type="password" class="form-input" :class="{ 'input-error': errors.password }" />
                  <ErrorMessage name="password" class="error-text" />
                  <span class="help-text">รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร</span>
                </div>
                <div class="form-group">
                  <label class="form-label">ยืนยันรหัสผ่านใหม่ *</label>
                  <Field name="password_confirmation" v-model="passwordForm.password_confirmation" type="password" class="form-input" :class="{ 'input-error': errors.password_confirmation }" />
                  <ErrorMessage name="password_confirmation" class="error-text" />
                </div>
                <button type="submit" :disabled="isSubmitting" class="btn btn-primary">{{ isSubmitting ? 'กำลังเปลี่ยน...' : 'เปลี่ยนรหัสผ่าน' }}</button>
              </Form>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'orders'" class="orders-section">
          <div v-if="ordersLoading" class="text-center" style="padding: 3rem 0;">
            <div class="loading-spinner" style="margin: 0 auto;"></div>
          </div>
          <div v-else-if="orders.length === 0" class="empty-state card">
            <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <h3 class="empty-title">ยังไม่มีรายการสั่งซื้อ</h3>
            <p class="empty-desc">คุณยังไม่มีรายการสั่งซื้อ เริ่มช้อปปิ้งเลย!</p>
            <router-link to="/products" class="btn btn-primary">เริ่มช้อปปิ้ง</router-link>
          </div>
          <div v-else class="orders-list">
            <div v-for="order in orders" :key="order.id" class="order-card card">
              <div class="card-body">
                <div class="order-header">
                  <div>
                    <div class="order-number">#{{ order.order_number }}</div>
                    <p class="order-date">{{ formatDate(order.created_at) }}</p>
                  </div>
                  <span class="badge" :class="getStatusClass(order.status)">{{ getStatusText(order.status) }}</span>
                </div>
                <div class="order-summary">
                  <div class="order-total">{{ formatPrice(order.total_amount) }}</div>
                  <p class="order-items-count">{{ order.items?.length || 0 }} รายการ</p>
                </div>
                <div class="order-actions">
                  <router-link v-if="order.status === 'pending_payment'" :to="`/order/${order.order_number}`" class="btn btn-primary btn-sm">ชำระเงิน</router-link>
                  <router-link :to="`/member/orders/${order.order_number}`" class="btn btn-outline btn-sm">ดูรายละเอียด</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import api from '../api';
import { useAuthStore } from '../stores/auth';
import { useNotification } from '../composables/useNotification';
import { getOrderStatusLabel } from '../utils/orderStatus';

const router = useRouter();
const authStore = useAuthStore();
const { showSuccess, handleApiError } = useNotification();

const loading = ref(true);
const activeTab = ref('profile');
const user = computed(() => authStore.user);
const orders = ref([]);
const ordersLoading = ref(false);

const tabs = [
  { id: 'profile', label: 'ข้อมูลส่วนตัว' },
  { id: 'address', label: 'ที่อยู่จัดส่ง' },
  { id: 'password', label: 'เปลี่ยนรหัสผ่าน' },
  { id: 'orders', label: 'รายการสั่งซื้อ' },
];

const profileForm = reactive({ name: '', email: '', phone: '' });
const addressForm = reactive({ address: '', district: '', province: '', postal_code: '' });
const passwordForm = reactive({ current_password: '', password: '', password_confirmation: '' });

const profileSchema = yup.object({
  name: yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
  email: yup.string().nullable().email('รูปแบบอีเมลไม่ถูกต้อง'),
  phone: yup.string().nullable().test('phone-format', 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก', (v) => !v || /^[0-9]{10}$/.test(v)),
});

const addressSchema = yup.object({
  address: yup.string().nullable().max(500, 'ที่อยู่ต้องไม่เกิน 500 ตัวอักษร'),
  district: yup.string().nullable().max(100),
  province: yup.string().nullable().max(100),
  postal_code: yup.string().nullable().matches(/^[0-9]{5}$|^$/, 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก'),
});

const passwordSchema = yup.object({
  current_password: yup.string().required('กรุณากรอกรหัสผ่านปัจจุบัน'),
  password: yup.string().required('กรุณากรอกรหัสผ่านใหม่').min(6, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'),
  password_confirmation: yup.string().required('กรุณายืนยันรหัสผ่าน').oneOf([yup.ref('password')], 'รหัสผ่านไม่ตรงกัน'),
});

onMounted(async () => {
  try {
    await authStore.fetchUser();
    if (user.value) {
      profileForm.name = user.value.name || '';
      profileForm.email = user.value.email || '';
      profileForm.phone = user.value.phone || '';
      addressForm.address = user.value.address || '';
      addressForm.district = user.value.district || '';
      addressForm.province = user.value.province || '';
      addressForm.postal_code = user.value.postal_code || '';
    }
    await fetchOrders();
  } catch (error) {
    console.error('Failed to fetch user', error);
  } finally {
    loading.value = false;
  }
});

const fetchOrders = async () => {
  ordersLoading.value = true;
  try {
    const response = await api.get('/member/orders');
    orders.value = response.data.data.data || response.data.data || [];
  } catch (error) {
    console.error('Failed to fetch orders', error);
  } finally {
    ordersLoading.value = false;
  }
};

const updateProfile = async (values, { setErrors }) => {
  try {
    const response = await api.put('/member/profile', profileForm);
    authStore.user = response.data.user;
    showSuccess('อัปเดตข้อมูลสำเร็จ');
  } catch (error) {
    handleApiError(error, setErrors);
  }
};

const updateAddress = async (values, { setErrors }) => {
  try {
    const response = await api.put('/member/profile', { name: profileForm.name, ...addressForm });
    authStore.user = response.data.user;
    showSuccess('อัปเดตที่อยู่สำเร็จ');
  } catch (error) {
    handleApiError(error, setErrors);
  }
};

const changePassword = async (values, { setErrors, resetForm }) => {
  try {
    await api.put('/member/password', passwordForm);
    showSuccess('เปลี่ยนรหัสผ่านสำเร็จ');
    passwordForm.current_password = '';
    passwordForm.password = '';
    passwordForm.password_confirmation = '';
    resetForm();
  } catch (error) {
    handleApiError(error, setErrors);
  }
};

const formatPrice = (price) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(price);
const formatDate = (dateString) => new Date(dateString).toLocaleString('th-TH');

const getStatusClass = (status) => {
  const classes = { 'pending_payment': 'badge-warning', 'pending_verification': 'badge-warning', 'paid': 'badge-info', 'processing': 'badge-warning', 'shipped': 'badge-success', 'delivered': 'badge-success', 'cancelled': 'badge-danger' };
  return classes[status] || 'badge-warning';
};

const getStatusText = (status) => getOrderStatusLabel(status);

const logout = async () => {
  await authStore.logout();
  router.push('/login');
};
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
.page-title { font-size: 1.75rem; font-weight: 700; color: var(--color-text); }
.logout-btn { color: #dc2626; }
.logout-btn:hover { background: #fee2e2; }
.tabs-nav { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; overflow-x: auto; padding-bottom: 0.5rem; border-bottom: 1px solid var(--color-border-light); }
.tab-btn { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; border: none; background: transparent; color: var(--color-text-light); font-size: 0.9375rem; font-weight: 500; cursor: pointer; border-radius: 0.5rem; white-space: nowrap; transition: all 0.2s ease; }
.tab-btn:hover { background: var(--color-surface); color: var(--color-text); }
.tab-btn.active { background: var(--color-primary); color: white; }
.card { background: var(--color-surface); border-radius: 0.75rem; box-shadow: var(--shadow-sm); }
.card-header { padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--color-border-light); }
.card-title { font-size: 1.125rem; font-weight: 600; color: var(--color-text); margin: 0; }
.card-subtitle { font-size: 0.875rem; color: var(--color-text-muted); margin-top: 0.25rem; }
.card-body { padding: 1.5rem; }
.form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
.form-group { margin-bottom: 1rem; }
.form-label { display: block; font-size: 0.875rem; font-weight: 500; color: var(--color-text); margin-bottom: 0.5rem; }
.form-input { width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--color-border); border-radius: 0.5rem; font-size: 1rem; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
.form-input:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 3px rgba(133, 94, 66, 0.1); }
.form-input:disabled { background: #f5f5f5; cursor: not-allowed; }
.form-textarea { resize: vertical; min-height: 80px; }
.input-error { border-color: #ef4444 !important; }
.error-text { display: block; color: #ef4444; font-size: 0.8125rem; margin-top: 0.25rem; }
.help-text { display: block; color: var(--color-text-muted); font-size: 0.8125rem; margin-top: 0.25rem; }
.empty-state { text-align: center; padding: 4rem 1rem; }
.empty-icon { width: 4rem; height: 4rem; margin: 0 auto 1rem; color: var(--color-text-muted); }
.empty-title { font-size: 1.25rem; font-weight: 600; color: var(--color-text); margin-bottom: 0.5rem; }
.empty-desc { color: var(--color-text-light); margin-bottom: 1.5rem; }
.orders-list { display: flex; flex-direction: column; gap: 1rem; }
.order-card { transition: box-shadow 0.2s ease; }
.order-card:hover { box-shadow: var(--shadow-md); }
.order-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid var(--color-border-light); }
.order-number { font-weight: 700; font-size: 1.125rem; color: var(--color-primary); }
.order-date { font-size: 0.875rem; color: var(--color-text-muted); }
.order-summary { margin-bottom: 1rem; }
.order-total { font-size: 1.25rem; font-weight: 700; color: var(--color-text); }
.order-items-count { font-size: 0.875rem; color: var(--color-text-muted); }
.order-actions { display: flex; gap: 0.75rem; justify-content: flex-end; }
@media (max-width: 640px) {
  .tabs-nav { gap: 0.25rem; }
  .tab-btn { padding: 0.625rem 1rem; font-size: 0.875rem; }
  .form-grid { grid-template-columns: 1fr; }
  .order-actions { flex-direction: column; }
  .order-actions .btn { width: 100%; justify-content: center; }
}
</style>
